import { call, put, select } from 'redux-saga/effects';
import { accessTokenExpired } from 'flowhawk-core';
import queryClient from '../queryClient';
import { buildResourceFetch, type RequestData, type ResourceParams } from '../Services/Api';
import type { RootState } from '../Redux';
import { type Attachments, type Kwargs, type Query, ResourceErrorInterface } from 'tg-resources';
import type { FetchResource as Resource } from '@tg-resources/fetch';
import type { QueryKey } from '@tanstack/react-query';
import type { SagaIterator } from 'redux-saga';
import type { ApiResponse } from '../Utils/apiTypes';

function* apiCall<TData = unknown>(resource: Resource, params: ResourceParams): SagaIterator<ApiResponse<TData>> {
  let result: TData;
  const { method, kwargs, query } = params;

  const token: string = yield select((state: RootState) => state.auth.accessToken);

  // Create new headers config because we might need to set token
  const headers: HeadersInit = {
    Accept: 'application/json',
  };

  // Add Authorization header unless withCredentials is false for the resource

  if (token && resource.config().withCredentials) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    if (method === 'fetch') {
      result = yield call([resource, method], kwargs, query, {
        headers,
      });
    } else {
      const { data, attachments } = params;
      result = yield call([resource, method], kwargs, data, query, attachments, {
        headers,
      });
    }
  } catch (error) {
    if (error instanceof ResourceErrorInterface || error instanceof Error) {
      return {
        error,
      };
    } else {
      return {
        error: new Error('Unknown apiCall error'),
      };
    }
  }

  return {
    result,
  };
}

export function* apiFetch<TData>(resource: Resource, kwargs?: Kwargs, query?: Query): SagaIterator<ApiResponse<TData>> {
  const { result, error } = yield call(apiCall<TData>, resource, { method: 'fetch', kwargs, query });

  if (error && error.statusCode === 401) {
    yield put(accessTokenExpired());
  }

  return { result, error };
}

export function* apiPost<TData>(
  resource: Resource,
  kwargs?: Kwargs,
  data?: RequestData,
  query?: Query,
  attachments?: Attachments,
): SagaIterator<ApiResponse<TData>> {
  const { result, error } = yield call(apiCall<TData>, resource, { method: 'post', kwargs, data, query, attachments });

  if (error && error.statusCode === 401) {
    yield put(accessTokenExpired());
  }

  return { result, error };
}

export function* apiDelete<TData>(
  resource: Resource,
  kwargs?: Kwargs,
  data?: RequestData,
  query?: Query,
  attachments?: Attachments,
): SagaIterator<ApiResponse<TData>> {
  const { result, error } = yield call(apiCall, resource, { method: 'del', kwargs, data, query, attachments });

  if (error && error.statusCode === 401) {
    yield put(accessTokenExpired());
  }

  return { result, error };
}

export function* apiPut<TData>(
  resource: Resource,
  kwargs?: Kwargs,
  data?: RequestData,
  query?: Query,
  attachments?: Attachments,
): SagaIterator<ApiResponse<TData>> {
  const { result, error } = yield call(apiCall, resource, { method: 'put', kwargs, data, query, attachments });

  if (error && error.statusCode === 401) {
    yield put(accessTokenExpired());
  }

  return { result, error };
}

export function* apiPatch<TData>(
  resource: Resource,
  kwargs?: Kwargs,
  data?: RequestData,
  query?: Query,
  attachments?: Attachments,
): SagaIterator<ApiResponse<TData>> {
  const { result, error } = yield call(apiCall, resource, { method: 'patch', kwargs, data, query, attachments });

  if (error && error.statusCode === 401) {
    yield put(accessTokenExpired());
  }

  return { result, error };
}

/**
 * Use React Query Client's caching mechanism to reuse data
 * from existing useQuery hooks and minimize refetching data
 */
export function* apiCachedFetch<TData = unknown>(
  queryKey: QueryKey,
  resource: Resource,
  kwargs: Kwargs,
  query?: Query,
): Generator<any, { result: TData | undefined; error: unknown }, any> {
  const token: string = yield select((state) => state.auth.accessToken);
  const fetchResource = buildResourceFetch(token);

  try {
    // Use fetchQuery instead of getQueryData/setQueryData to ensure that
    // duplicate requests for this query are not created with useQuery instances
    const data: TData = yield call(() =>
      queryClient.fetchQuery<TData>({
        queryKey,
        queryFn: () => fetchResource(resource, { method: 'fetch', kwargs, query }),
      }),
    );

    return { result: data, error: undefined };
  } catch (error) {
    return { result: undefined, error };
  }
}
